import * as _ from 'lodash';
import * as semver from 'semver';
import * as storage from 'shared/childupp-local-storage';

const VERSION_STORAGE_KEY_PREFIX = 'childupp_versions';

/**
 * Retrieves the locally stored map of versions. If no map is
 * available (because it has not been initialized yet), this
 * method retsolves into an empty map.
 *
 * @private
 * @return {Promise.<Object.<string, object>>}
 */
async function getStoredVersionMap() {
    const versions = await storage.get(VERSION_STORAGE_KEY_PREFIX);
    return versions || {};
}

/**
 * Stores the provided map as the new available versions map. Further
 * calls to {@link getStoredVersionMap} will resolve into said map.
 *
 * @param {Object.<string, object>} map
 * @return {Promise.<void>}
 */
async function setStoredVersionMap(map) {
    await storage.save(VERSION_STORAGE_KEY_PREFIX, map);
}

/**
 * Returns the list of SemVer version identifier of locally
 * installed versions.
 *
 * @return {Promise.<string[]>}
 */
export async function getVersions() {
    const versions = await getStoredVersionMap();
    return Object.keys(versions);
}

/**
 * Returns the SemVer identifier for the latest locally
 * avilable version.
 *
 * @return {Promise.<string>}
 */
export async function getLatestVersion() {
    const versions = await getVersions();
    // If no local versions are available, assume current running
    // version is the one to be used.
    if (!versions || versions.length === 0)
        return __VERSION__;
    // Sort the versions by ascending and return the latest
    return versions.sort(semver.compare).pop();
}

/**
 * Retrieves the local filesystem uri that points to the root file
 * where the given version is installed (typically index.html).
 *
 * If given version is not installed, resolves into null.
 *
 * @param  {string} version - SemVer identifier
 * @return {Promise.<string>}
 */
export async function getVersionInstallationPath(version) {
    const versions = await getStoredVersionMap();
    if (versions.hasOwnProperty(version))
        return versions[version]['installationPath'];
    else
        return null;
}

/**
 * Sets the installation path for the provided version. Following
 * calls to {@link getVersionInstallationPath} will return the just
 * provided path.
 *
 * @param {string} version - SemVer identifier
 * @param {string} path    - Full URI local path
 * @return {Promise.<void>}
 */
export async function setVersionInstallationPath(version, path) {
    const versions = await getStoredVersionMap();
    const updatedVersions = _.merge({}, versions, {
        [version]: { installationPath: path },
    });
    await setStoredVersionMap(updatedVersions);
    return Promise.resolve(updatedVersions);
}



// WEBPACK FOOTER //
// ./src/shared/childupp-auto-update/cordova-auto-update/versions-repository.js